import React, { useState, useEffect } from 'react';
import { FiX, FiChevronLeft, FiChevronRight, FiDownload, FiMaximize2 } from 'react-icons/fi';
import { Document } from '../services/documents.service';
import { api } from '../config/api';
import { safeFormatDate } from '../utils/dateUtils';

interface ImageGalleryInlineProps {
  documents: Document[];
}

interface ImageModalProps {
  document: Document;
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  currentIndex: number;
  totalImages: number;
}

const ImageModal: React.FC<ImageModalProps> = ({ 
  document: doc, 
  imageUrl, 
  isOpen, 
  onClose, 
  onPrevious, 
  onNext,
  currentIndex,
  totalImages
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrevious) onPrevious();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };

    if (isOpen) {
      window.document.addEventListener('keydown', handleEscape);
      window.document.body.style.overflow = 'hidden';
    }

    return () => {
      window.document.removeEventListener('keydown', handleEscape);
      window.document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, onPrevious, onNext]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent">
        <div className="flex justify-between items-center p-4">
          <div className="text-white">
            <h3 className="text-lg font-medium">{doc.filename}</h3>
            <p className="text-sm text-gray-300">
              {safeFormatDate(doc.created_at)} • 
              {currentIndex + 1} of {totalImages}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <FiDownload className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      {onPrevious && (
        <button
          onClick={onPrevious}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:bg-white/20 rounded-full transition-colors z-10"
        >
          <FiChevronLeft className="w-6 h-6" />
        </button>
      )}
      
      {onNext && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:bg-white/20 rounded-full transition-colors z-10"
        >
          <FiChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Image */}
      <div className="max-w-screen max-h-screen p-16">
        <img
          src={imageUrl}
          alt={doc.filename}
          className="max-w-full max-h-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Click outside to close */}
      <div 
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>
  );
};

export const ImageGalleryInline: React.FC<ImageGalleryInlineProps> = ({ documents }) => {
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const INITIAL_LOAD_COUNT = 8;
  const displayedDocuments = showAll ? documents : documents.slice(0, INITIAL_LOAD_COUNT);

  useEffect(() => {
    // Load first batch of images
    loadImages(displayedDocuments.slice(0, 4));
    
    return () => {
      // Cleanup blob URLs
      imageUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [documents]);

  const loadImages = async (docsToLoad: Document[]) => {
    const newLoadingSet = new Set(loadingImages);
    
    docsToLoad.forEach(doc => {
      newLoadingSet.add(doc.id);
    });
    setLoadingImages(newLoadingSet);

    for (const doc of docsToLoad) {
      if (imageUrls.has(doc.id)) continue; // Already loaded
      
      try {
        const token = localStorage.getItem('accessToken');
        const response = await api.get(`/documents/${doc.id}/download`, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const blob = new Blob([response.data], { type: doc.mime_type || 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        
        setImageUrls(prev => new Map(prev).set(doc.id, url));
        setLoadedCount(prev => prev + 1);
      } catch (error) {
        console.error(`Failed to load image ${doc.id}:`, error);
      } finally {
        setLoadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(doc.id);
          return newSet;
        });
      }
    }
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    // Load the image if not already loaded
    const doc = displayedDocuments[index];
    if (!imageUrls.has(doc.id)) {
      loadImages([doc]);
    }
  };

  const handleShowMore = () => {
    setShowAll(true);
    // Load remaining images
    const remainingDocs = documents.slice(INITIAL_LOAD_COUNT);
    loadImages(remainingDocs);
  };

  const handlePrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < displayedDocuments.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <FiMaximize2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No images found for this property</p>
      </div>
    );
  }

  return (
    <>
      <div className="mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {displayedDocuments.map((doc, index) => {
            const imageUrl = imageUrls.get(doc.id);
            const isLoading = loadingImages.has(doc.id);
            
            return (
              <div 
                key={doc.id}
                className="group relative aspect-square bg-gray-800 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all"
                onClick={() => handleImageClick(index)}
              >
                {imageUrl ? (
                  <>
                    <img
                      src={imageUrl}
                      alt={doc.filename}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <FiMaximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : isLoading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-700">
                    <FiMaximize2 className="w-8 h-8 text-gray-500" />
                  </div>
                )}
                
                {/* File info overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                  <p className="text-white text-xs truncate">{doc.filename}</p>
                  <p className="text-gray-300 text-xs">
                    {safeFormatDate(doc.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show More Button */}
        {!showAll && documents.length > INITIAL_LOAD_COUNT && (
          <div className="text-center mt-6">
            <button
              onClick={handleShowMore}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Show {documents.length - INITIAL_LOAD_COUNT} more images
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="mt-4 text-sm text-gray-400 text-center">
          Showing {displayedDocuments.length} of {documents.length} images
          {loadedCount > 0 && ` • ${loadedCount} loaded`}
        </div>
      </div>

      {/* Modal */}
      {selectedImageIndex !== null && (
        <ImageModal
          document={displayedDocuments[selectedImageIndex]}
          imageUrl={imageUrls.get(displayedDocuments[selectedImageIndex].id) || ''}
          isOpen={true}
          onClose={() => setSelectedImageIndex(null)}
          onPrevious={selectedImageIndex > 0 ? handlePrevious : undefined}
          onNext={selectedImageIndex < displayedDocuments.length - 1 ? handleNext : undefined}
          currentIndex={selectedImageIndex}
          totalImages={displayedDocuments.length}
        />
      )}
    </>
  );
};